import React, { useState, useEffect, useRef, memo, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { getMqttInstance, decodeMqttPayload } from '../../../services/mqtt/mqttService';
import AgentConfigPanel from './AgentConfigPanel';
import { useAgentStore } from '../../../store/agentStore';
import { updateAgent as updateAgentApi, updateAgentConfig as persistAgentConfig } from '../../../api/agentApi';

// Constants
const AGENT_TOPIC_PATTERN = 'agent/+/status'; // Wildcard topic for all agent statuses
const CONNECTION_TIMEOUT = 15000; // Increased timeout
const CLEANUP_TIMEOUT = 7000; // Increased cleanup timeout
const MESSAGE_THROTTLE_MS = 1000; // Reduced throttle time
const MAX_DUPLICATE_MESSAGES = 5; // Increased duplicate threshold

/**
 * Enhanced Message throttling utility to prevent spam
 */
class MessageThrottle {
  constructor(throttleMs = MESSAGE_THROTTLE_MS, maxDuplicates = MAX_DUPLICATE_MESSAGES) {
    this.throttleMs = throttleMs;
    this.maxDuplicates = maxDuplicates;
    this.lastMessage = null;
    this.lastMessageTime = 0;
    this.duplicateCount = 0;
    this.throttledUntil = 0;
    this.processedMessages = new Set();
    this.cleanupInterval = null;
    this.startCleanup();
  }

  startCleanup() {
    // Clean up old processed messages every 10 seconds instead of 30
    this.cleanupInterval = setInterval(() => {
      this.processedMessages.clear();
      console.log('Cleaned up processed messages cache');
    }, 10000);
  }

  shouldProcess(message) {
    const now = Date.now();
    const messageHash = this.hashMessage(message);

    // Check if we've already processed this exact message recently
    if (this.processedMessages.has(messageHash)) {
      return false;
    }

    // Always allow online/offline status changes to go through
    if (message.data && (message.data.status === 'online' || message.data.status === 'offline')) {
      return true;
    }

    // If we're still in throttle period, reject
    if (now < this.throttledUntil) {
      return false;
    }

    // Check if this is a duplicate message
    if (messageHash === this.lastMessage) {
      this.duplicateCount++;

      // If we've seen too many duplicates, throttle for a while
      // But be more lenient with duplicates now
      if (this.duplicateCount > this.maxDuplicates) {
        this.throttledUntil = now + this.throttleMs;
        console.log(`Throttling messages for ${this.throttleMs}ms due to duplicates`);
        this.duplicateCount = 0;
        return false;
      }

      return true;
    } else {
      // New message, reset duplicate counter
      this.duplicateCount = 0;
    }

    // Check time-based throttling for same messages
    if (now - this.lastMessageTime < this.throttleMs && messageHash === this.lastMessage) {
      return false;
    }

    // Mark as processed
    this.processedMessages.add(messageHash);
    this.lastMessage = messageHash;
    this.lastMessageTime = now;
    return true;
  }

  hashMessage(message) {
    try {
      const data = typeof message.data === 'string' ? JSON.parse(message.data) : message.data;
      // Create hash excluding timestamp fields and other frequently changing data
      const { lastSeen, timestamp, messageId, clientId, ...stableData } = data;
      return JSON.stringify(stableData) + '_' + (message.name || '');
    } catch {
      return JSON.stringify(message.data) + '_' + (message.name || '') + '_' + Date.now();
    }
  }

  reset() {
    this.lastMessage = null;
    this.lastMessageTime = 0;
    this.duplicateCount = 0;
    this.throttledUntil = 0;
    this.processedMessages.clear();
  }

  destroy() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.reset();
  }
}

/**
 * Enhanced hook for managing MQTT subscription with duplicate prevention
 */
const useMqttSubscription = (mqttReady, onMessage, onError) => {
  console.log('useMqttSubscription: Initializing with mqttReady:', mqttReady);
  const mqttClientRef = useRef(null);
  const mountedRef = useRef(true);
  const cleanupTimeoutRef = useRef(null);
  const connectionTimeoutRef = useRef(null);
  const messageThrottleRef = useRef(new MessageThrottle());
  const subscriptionIdRef = useRef(null);
  const lastConnectionTimeRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (messageThrottleRef.current) {
        messageThrottleRef.current.destroy();
      }
    };
  }, []);

  const cleanup = useCallback(async () => {
    console.log('Starting MQTT cleanup...');

    // Clear timeouts
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }

    // Reset message throttle
    if (messageThrottleRef.current) {
      messageThrottleRef.current.reset();
    }

    const mqtt = mqttClientRef.current;
    if (!mqtt) {
      console.log('No MQTT client to cleanup');
      return;
    }

    const oldSubscriptionId = subscriptionIdRef.current;
    mqttClientRef.current = null;
    subscriptionIdRef.current = null;

    try {
      const cleanupPromise = new Promise(resolve => {
        cleanupTimeoutRef.current = setTimeout(() => {
          console.warn('MQTT cleanup timed out');
          resolve();
        }, CLEANUP_TIMEOUT);

        const performCleanup = async () => {
          try {
            // Unsubscribe from MQTT topics
            console.log(`Unsubscribing from MQTT topics (${oldSubscriptionId})...`);

            // Remove all message listeners
            mqtt.removeAllListeners('message');

            // Unsubscribe from agent status topic
            mqtt.unsubscribe(AGENT_TOPIC_PATTERN, err => {
              if (err) {
                console.warn('Error unsubscribing from topic:', err);
              } else {
                console.log(`Unsubscribed from ${AGENT_TOPIC_PATTERN} successfully`);
              }
            });

            // Note: We're not ending the MQTT connection here because it's managed by the mqttService
            // and may be used by other components
          } catch (error) {
            console.warn('Error during MQTT cleanup:', error);
          } finally {
            if (cleanupTimeoutRef.current) {
              clearTimeout(cleanupTimeoutRef.current);
              cleanupTimeoutRef.current = null;
            }
            resolve();
          }
        };

        performCleanup();
      });

      await cleanupPromise;
    } catch (error) {
      console.warn('MQTT cleanup failed:', error);
    }

    console.log('MQTT cleanup completed');
  }, []);

  useEffect(() => {
    console.log('useEffect: MQTT connection status - ready:', mqttReady);

    if (!mqttReady) {
      console.log('MQTT not ready, attempting to initialize...');
      // Try to get MQTT instance to force initialization if not ready
      getMqttInstance()
        .then(() => {
          console.log('MQTT instance initialized after retry');
        })
        .catch(err => {
          console.error('Failed to initialize MQTT:', err);
          if (onError) onError(err);
        });
      return;
    }

    const setupSubscription = async () => {
      try {
        const now = Date.now();

        // Prevent rapid reconnections
        if (now - lastConnectionTimeRef.current < 5000) {
          console.log('Preventing rapid reconnection, waiting...');
          await new Promise(resolve => setTimeout(resolve, 2000));
        }

        lastConnectionTimeRef.current = now;

        console.log(`Setting up MQTT subscription for agent status topics`);

        // Cleanup any existing connection first
        await cleanup();

        if (!mountedRef.current) {
          console.log('Component unmounted during setup');
          return;
        }

        const mqtt = await getMqttInstance();
        if (!mountedRef.current) return;

        // Store MQTT client reference
        mqttClientRef.current = mqtt;

        // Generate unique subscription ID to prevent duplicates
        const subscriptionId = `sub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        subscriptionIdRef.current = subscriptionId;

        console.log(`Created subscription: ${subscriptionId}`);

        // Set connection timeout
        connectionTimeoutRef.current = setTimeout(() => {
          if (mountedRef.current && subscriptionIdRef.current === subscriptionId) {
            onError('Connection timeout - failed to connect to MQTT broker');
          }
        }, CONNECTION_TIMEOUT);

        // Handle MQTT connection states
        mqtt.on('connect', () => {
          if (connectionTimeoutRef.current) {
            clearTimeout(connectionTimeoutRef.current);
            connectionTimeoutRef.current = null;
          }
          console.log(`MQTT connected successfully (${subscriptionId})`);
        });

        mqtt.on('error', error => {
          console.error(`MQTT error (${subscriptionId}):`, error);
          if (mountedRef.current && subscriptionIdRef.current === subscriptionId) {
            onError(`MQTT connection error: ${error.message}`);
          }
        });

        mqtt.on('close', () => {
          console.log(`MQTT connection closed (${subscriptionId})`);
        });

        // MQTT should already be connected at this point
        if (!mountedRef.current || subscriptionIdRef.current !== subscriptionId) {
          console.log('Component unmounted or subscription changed during setup');
          return;
        }

        // Subscribe to status messages with enhanced throttling - using wildcard topic
        console.log(`Subscribing to topic: ${AGENT_TOPIC_PATTERN}...`);
        mqtt.subscribe(AGENT_TOPIC_PATTERN, { qos: 1 }, err => {
          if (err) {
            console.error(`Error subscribing to ${AGENT_TOPIC_PATTERN}:`, err);
            if (mountedRef.current && subscriptionIdRef.current === subscriptionId) {
              onError(`Failed to subscribe to topic: ${err.message}`);
            }
          } else {
            console.log(`Successfully subscribed to ${AGENT_TOPIC_PATTERN}`);
          }
        });

        // Set up message handler
        const messageHandler = (topic, messageBuffer) => {
          // Check if this subscription is still active
          if (!mountedRef.current || subscriptionIdRef.current !== subscriptionId) {
            console.log('Ignoring message from inactive subscription');
            return;
          }

          try {
            // Parse message using safe decoder
            const messageStr = decodeMqttPayload(messageBuffer);
            let message;

            try {
              message = {
                data: JSON.parse(messageStr),
                topic: topic,
              };

              // Extract agentId from topic if not present in message
              // Topic format: agent/{agentId}/status
              if (!message.data.agentId && !message.data.id) {
                const topicParts = topic.split('/');
                if (topicParts.length >= 3) {
                  message.data.agentId = topicParts[1];
                }
              }
            } catch (e) {
              message = {
                data: messageStr,
                topic: topic,
              };
            }

            // Apply message throttling
            if (!messageThrottleRef.current.shouldProcess(message)) {
              return; // Silently ignore throttled messages
            }

            console.log(`Processing message (${subscriptionId}):`, {
              topic: topic,
              agentId: message.data?.agentId || message.data?.id,
              status: message.data?.status,
            });
            onMessage(message);
          } catch (error) {
            console.error('Error processing MQTT message:', error);
          }
        };

        // Register message handler
        mqtt.on('message', messageHandler);

        if (!mountedRef.current || subscriptionIdRef.current !== subscriptionId) {
          console.log('Component unmounted or subscription changed during setup');
          return;
        }

        // Clear connection timeout on successful setup
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }

        console.log(`MQTT subscription setup completed successfully (${subscriptionId})`);
      } catch (error) {
        console.error('Error setting up MQTT subscription:', error);
        if (mountedRef.current) {
          onError(`Failed to connect to MQTT broker: ${error.message}`);
        }
      }
    };

    setupSubscription();
    return cleanup;
  }, [mqttReady, onMessage, onError, cleanup]);

  return { cleanup };
};

/**
 * Enhanced agent state management with better change detection and offline timeout
 */
const useAgentState = () => {
  console.log('useAgentState: Initializing agent state');
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const toastIdRef = useRef(null);
  const lastProcessedHashRef = useRef(new Map());
  const messageCountRef = useRef(0);

  // Keep track of last activity time for each agent
  const lastActivityTimeRef = useRef(new Map());

  // Timeout duration in milliseconds (30 seconds)
  const OFFLINE_TIMEOUT_MS = 30000;

  const processMessage = useCallback(message => {
    try {
      messageCountRef.current++;

      // Handle both direct object messages and messages with a data property
      const data =
        message && typeof message === 'object' && 'data' in message
          ? typeof message.data === 'string'
            ? JSON.parse(message.data)
            : message.data
          : message; // If message is already the data object

      if (!data || typeof data !== 'object') {
        throw new Error(`Invalid message data format: ${typeof data}`);
      }

      const agentId = data.agentId || data.id;
      if (!agentId) {
        console.warn('Received agent status message without agent id; ignoring');
        return;
      }

      // Create a stable hash for change detection (excluding timestamp fields)
      const { lastSeen, timestamp, messageId, clientId, ...stableData } = data;
      const dataHash = JSON.stringify(stableData);

      // Check if we've already processed this exact data
      // But only skip if it's not an online/offline status change
      const lastHash = lastProcessedHashRef.current.get(agentId);
      if (lastHash === dataHash && data.status !== 'online' && data.status !== 'offline') {
        console.log(
          `Skipping duplicate message for agent ${agentId} (total processed: ${messageCountRef.current})`
        );
        return;
      }

      lastProcessedHashRef.current.set(agentId, dataHash);

      const agent = {
        id: agentId,
        name: data.name || data.agentName || `Agent ${agentId}`,
        status: data.status || 'unknown',
        features: Array.isArray(data.features) ? data.features : [],
        lastSeen: new Date().toISOString(),
        // Store original data for configuration
        originalData: data,
      };

      console.log(`Processing agent update for ${agentId} (msg #${messageCountRef.current}):`, {
        name: agent.name,
        status: agent.status,
        features: agent.features,
      });

      // Update last activity time for this agent
      lastActivityTimeRef.current.set(agentId, Date.now());

      // Always update agent when receiving status messages
      setAgents(prevAgents => {
        const existingIndex = prevAgents.findIndex(a => a.id === agentId);

        if (existingIndex >= 0) {
          const existingAgent = prevAgents[existingIndex];

          // For status changes, always update
          if (agent.status !== existingAgent.status) {
            console.log(
              `Agent ${agentId} status changed from ${existingAgent.status} to ${agent.status}`
            );

            // Notify about status change in UI
            if (agent.status === 'online') {
              toast.success(`Agent ${agentId} is now online`, {
                autoClose: 3000,
                toastId: `agent-online-${agentId}`,
              });
            } else if (agent.status === 'offline') {
              toast.warn(`Agent ${agentId} is now offline`, {
                autoClose: 5000,
                toastId: `agent-offline-${agentId}`,
              });
            }

            // Always update for status changes
            const newAgents = [...prevAgents];
            newAgents[existingIndex] = {
              ...existingAgent,
              ...agent,
              // Preserve the original data with updated fields
              originalData: { ...existingAgent.originalData, ...agent.originalData },
            };
            return newAgents;
          }

          // For other changes, check if they're significant
          const hasChanged = ['name', 'features'].some(key => {
            if (key === 'features') {
              return JSON.stringify(existingAgent[key]) !== JSON.stringify(agent[key]);
            }
            return existingAgent[key] !== agent[key];
          });

          if (!hasChanged) {
            // Only update lastSeen without triggering re-render
            const newAgents = [...prevAgents];
            newAgents[existingIndex] = {
              ...existingAgent,
              lastSeen: agent.lastSeen,
              // Preserve original data but update with new fields
              originalData: { ...existingAgent.originalData, ...agent.originalData },
            };
            return newAgents;
          }

          console.log(`Agent ${agentId} has significant changes, updating state`);
          const newAgents = [...prevAgents];
          newAgents[existingIndex] = {
            ...existingAgent,
            ...agent,
            // Preserve original data but update with new fields
            originalData: { ...existingAgent.originalData, ...agent.originalData },
          };
          return newAgents;
        }

        // New agent - show toast notification (only once per agent)
        const toastKey = `agent-${agentId}`;
        if (!toastIdRef.current || toastIdRef.current !== toastKey) {
          toastIdRef.current = toastKey;
          toast.success(`Agent ${agentId} connected`, {
            toastId: toastKey,
            onClose: () => {
              toastIdRef.current = null;
            },
          });
        }

        console.log(`Adding new agent ${agentId} to state`);
        return [...prevAgents, agent];
      });

      // Clear loading state on first successful message
      setLoading(false);
      setError(null);
    } catch (err) {
      console.error('Error processing status message:', err);
      setError('Failed to process agent data');
    }
  }, []);

  const handleError = useCallback(errorMessage => {
    setError(errorMessage);
    setLoading(false);
  }, []);

  const resetError = useCallback(() => {
    setError(null);
    lastProcessedHashRef.current.clear();
    messageCountRef.current = 0;
  }, []);

  const sendAgentConfig = useCallback(async (agentId, config) => {
    try {
      console.log(`Sending configuration to agent ${agentId}:`, config);

      const mqtt = await getMqttInstance();
      const configTopic = `agent/${agentId}/config`;

      // Publish configuration change
      mqtt.publish(
        configTopic,
        JSON.stringify({
          agentId,
          config,
          timestamp: Date.now(),
          from: 'DirectAgentView',
        }),
        { qos: 1 }
      );

      toast.success('Configuration sent to agent');
      return true;
    } catch (error) {
      console.error('Error sending agent config:', error);
      toast.error('Failed to send configuration to agent');
      return false;
    }
  }, []);

  // Check agent status periodically and mark as offline if no updates received
  useEffect(() => {
    const checkAgentStatus = () => {
      const now = Date.now();
      const offlineAgents = [];

      setAgents(prevAgents => {
        let hasChanges = false;
        const newAgents = [...prevAgents];

        newAgents.forEach((agent, index) => {
          // Get last activity time for this agent
          const lastActivity = lastActivityTimeRef.current.get(agent.id) || 0;

          // Check if agent has timed out (no activity within timeout period)
          if (agent.status !== 'offline' && now - lastActivity > OFFLINE_TIMEOUT_MS) {
            console.log(
              `Agent ${agent.id} timed out (no activity for ${Math.floor((now - lastActivity) / 1000)}s), marking as offline`
            );
            newAgents[index] = {
              ...agent,
              status: 'offline',
            };
            offlineAgents.push(agent.id);
            hasChanges = true;
          }
        });

        return hasChanges ? newAgents : prevAgents;
      });

      // Show notifications for timed-out agents
      offlineAgents.forEach(agentId => {
        toast.warn(
          `Agent ${agentId} appears to be offline (no activity for ${Math.floor(OFFLINE_TIMEOUT_MS / 1000)}s)`,
          {
            autoClose: 5000,
            toastId: `agent-timeout-${agentId}`,
          }
        );
      });
    };

    // Set up interval to check agent status every 10 seconds
    const statusCheckInterval = setInterval(checkAgentStatus, 10000);

    return () => {
      clearInterval(statusCheckInterval);
    };
  }, []);

  return {
    agents,
    loading,
    error,
    processMessage,
    handleError,
    resetError,
    setLoading,
    sendAgentConfig,
  };
};

/**
 * DirectAgentView Component with enhanced message spam prevention
 */
const DirectAgentView = ({ mqttReady, mqttStatus, connectionError }) => {
  console.log('DirectAgentView mounted with mqttReady:', mqttReady, 'status:', mqttStatus);

  // Use persistent agent store instead of local state
  const { agents, loading, error, processMessage, resetError, setLoading } = useAgentStore();
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const messageThrottle = useRef(new MessageThrottle());

  // Effect for managing loading state based on MQTT connection
  useEffect(() => {
    console.log('useEffect (loading): mqttReady:', mqttReady, 'status:', mqttStatus);
    if (!mqttReady || mqttStatus !== 'connected') {
      setLoading(true);
    } else if (mqttReady && mqttStatus === 'connected') {
      // Only set loading to false if agents array also has content or if we decide initial connection means not loading
      // For now, let message processing primarily handle setting loading to false once data arrives.
      // This effect primarily ensures we are in a loading state if not connected.
      // setLoading(false); // Potentially set this, or let message handler do it.
    }
  }, [mqttReady, mqttStatus, setLoading]);

  // Force verification of agent statuses on component mount
  useEffect(() => {
    // Only run this if we have agents
    if (!agents || agents.length === 0) return;

    console.log(
      'DirectAgentView: Verifying agent statuses on mount...',
      agents.length,
      'agents found'
    );
    const now = new Date();

    // Check for stale agents (using current time) and mark as offline
    // This is more direct than waiting for the agentStore timer
    agents.forEach(agent => {
      if (agent.status !== 'offline') {
        // Check timestamp in message (if exists)
        if (agent.timestamp) {
          try {
            const msgTimestamp = new Date(agent.timestamp);
            const ageSeconds = Math.round((now - msgTimestamp) / 1000);
            console.log(
              `Agent ${agent.id}: status=${agent.status}, message timestamp=${agent.timestamp}, age=${ageSeconds}s`
            );

            // If older than 5 minutes, probably offline
            if (ageSeconds > 300) {
              // 5 minutes
              console.log(
                `Forcing agent ${agent.id} offline - message timestamp too old (${ageSeconds}s)`
              );
              // Update agent status through store API
              processMessage({
                id: agent.id,
                status: 'offline',
                forceOffline: true,
              });
            }
          } catch (err) {
            console.error(`Error parsing timestamp for ${agent.id}:`, err);
          }
        } else {
          console.log(`Agent ${agent.id}: No timestamp available`);
        }
      }
    });
  }, [agents, processMessage]);

  // Memoized MQTT message handler
  const handleMqttMessage = useCallback(
    (topic, message) => {
      try {
        console.log('Received MQTT message on topic:', topic);
        // Use centralized safe decoder
        const messageContent = decodeMqttPayload(message);

        let parsedMessage;
        try {
          parsedMessage =
            typeof messageContent === 'string' ? JSON.parse(messageContent) : messageContent;
        } catch (e) {
          console.error('Failed to parse MQTT message:', e, 'Raw message:', messageContent);
          return;
        }

        if (messageThrottle.current.shouldProcess(parsedMessage)) {
          console.log('Processing MQTT message:', parsedMessage);
          setLoading(false); // Data is being processed, so not loading anymore
          processMessage(parsedMessage);
        } else {
          console.log('Throttled duplicate MQTT message');
        }
      } catch (error) {
        console.error('Error handling MQTT message:', error);
      }
    },
    [processMessage, setLoading]
  ); // Dependencies for useCallback

  // Effect for MQTT client setup, subscription, and cleanup
  useEffect(() => {
    console.log('useEffect (MQTT setup): mqttReady:', mqttReady);
    if (!mqttReady) {
      // We are not ready to set up MQTT yet.
      // The loading state effect above should handle setting loading to true.
      return;
    }

    let mqttClientInstance;
    const setupMqtt = async () => {
      try {
        console.log('Attempting to get MQTT instance...');
        mqttClientInstance = await getMqttInstance();
        if (!mqttClientInstance) {
          console.error('Failed to get MQTT client instance for subscription');
          setLoading(true); // Still loading if instance failed
          return;
        }
        console.log('MQTT instance obtained, subscribing...');
        const topic = 'agent/+/status';
        mqttClientInstance.subscribe(topic, { qos: 1 });
        mqttClientInstance.on('message', handleMqttMessage);
        console.log('Subscribed to MQTT topic and message handler attached.');
        // Initial data might not have arrived, so keep loading true until first message or explicit connected state
        // setLoading(true); // Reconsider if this is needed here, loading effect handles initial state.
      } catch (error) {
        console.error('Error in setupMqtt:', error);
        setLoading(true); // Error during setup, so still loading/error state
      }
    };

    setupMqtt();

    return () => {
      if (mqttClientInstance) {
        console.log('Cleaning up MQTT subscription from DirectAgentView');
        mqttClientInstance.off('message', handleMqttMessage);
        // Don't reset agent state on unmount - it's now managed by the persistent store
        // Consider if unsubscribe is always needed or if client handles it on disconnect/reconnect
        // mqttClientInstance.unsubscribe('agent/+/status');
      }
    };
  }, [mqttReady, handleMqttMessage, setLoading]); // Added setLoading here for safety in error cases

  // Render connection status and error messages
  const renderConnectionStatus = () => {
    // Don't show any status if we're connected and have no errors
    if (mqttReady && mqttStatus === 'connected' && !connectionError) {
      return null;
    }

    if (connectionError) {
      return (
        <div
          className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded"
          role="alert"
        >
          <div className="flex items-center">
            <svg
              className="h-5 w-5 text-red-500 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <p className="font-bold">Connection Error</p>
              <p className="text-sm">{connectionError}</p>
            </div>
          </div>
        </div>
      );
    }

    // Show connection status when not connected
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4 rounded">
        <div className="flex items-center">
          <svg
            className="h-5 w-5 text-yellow-500 mr-2 animate-pulse"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <p className="font-medium">
              {mqttStatus === 'connecting'
                ? 'Connecting to MQTT broker...'
                : 'Waiting for MQTT connection...'}
            </p>
            <p className="text-sm text-yellow-700">Status: {mqttStatus || 'initializing'}</p>
          </div>
        </div>
      </div>
    );
  };

  // Handle config modal close
  const handleCloseConfig = useCallback(() => {
    setIsConfigModalOpen(false);
    // Clear selected agent after animation completes
    setTimeout(() => setSelectedAgent(null), 300);
  }, []);

  // Handle config save
  const handleConfigSave = useCallback(
    async (agentId, partialConfig) => {
      try {
        // Find the current agent from selectedAgent or store
        const currentAgent =
          (selectedAgent &&
            (selectedAgent.id === agentId || selectedAgent.agent_id === agentId)
            ? selectedAgent
            : agents.find(a => (a.id || a.agent_id) === agentId)) || {};

        // Merge settings/config
        // Normalize heartbeat to ms if provided in seconds
        const normalizeHeartbeat = value => {
          if (typeof value !== 'number' || Number.isNaN(value)) return value;
          return value < 1000 ? value * 1000 : value; // treat small values as seconds
        };

        const mergedSettings = {
          ...(currentAgent.settings || currentAgent.config || {}),
          heartbeat_interval: normalizeHeartbeat(
            partialConfig.heartbeat_interval ?? currentAgent?.settings?.heartbeat_interval
          ),
          tenant_sip_id:
            partialConfig.tenant_sip_id ?? currentAgent?.settings?.tenant_sip_id,
          rest_id: partialConfig.rest_id ?? currentAgent?.settings?.rest_id,
          tenant_id: partialConfig.tenant_id ?? currentAgent?.settings?.tenant_id,
          school_building_name:
            partialConfig.school_building_name ?? currentAgent?.settings?.school_building_name,
        };

        // Build full agent payload expected by transformAgentToApi()
        const mergedAgent = {
          agentId: currentAgent.agentId || currentAgent.id || currentAgent.agent_id || agentId,
          agentName:
            partialConfig.name ?? currentAgent.agentName ?? currentAgent.name ?? 'Unnamed Agent',
          agentTenant: currentAgent.agentTenant ?? partialConfig.tenant_id,
          deviceInfo: currentAgent.deviceInfo || {},
          features: partialConfig.features ?? currentAgent.features ?? [],
          tags: currentAgent.tags ?? [],
          settings: mergedSettings,
        };

        // Persist via API
        const updated = await updateAgentApi(mergedAgent.agentId, mergedAgent);
        
        // Persist full config (tenant fields included) to Firestore without MQTT publish
        try {
          await persistAgentConfig(mergedAgent.agentId, {
            features: mergedAgent.features || {},
            settings: mergedAgent.settings || {},
            publish_update: false,
          });
        } catch (cfgErr) {
          console.error('Failed to persist agent config to backend:', cfgErr);
          toast.error('Configuration applied, but failed to persist settings to backend.');
        }

        // Update local selected agent state for the modal
        setSelectedAgent(prev => ({
          ...(prev || {}),
          id: updated.agentId || agentId,
          name: updated.agentName || mergedAgent.agentName,
          features: updated.features || mergedAgent.features,
          settings: updated.settings || mergedAgent.settings,
          agentTenant: updated.agentTenant ?? mergedAgent.agentTenant,
          tenant_sip_id: updated.tenant_sip_id ?? mergedSettings.tenant_sip_id,
          rest_id: updated.rest_id ?? mergedSettings.rest_id,
          tenant_id: updated.tenant_id ?? mergedSettings.tenant_id,
          school_building_name:
            updated.school_building_name ?? mergedSettings.school_building_name,
          lastUpdated: new Date().toISOString(),
        }));

        // Update global agent list via store (expects an `id` field)
        processMessage({
          id: updated.agentId || agentId,
          name: updated.agentName || mergedAgent.agentName,
          features: updated.features || mergedAgent.features,
          settings: updated.settings || mergedAgent.settings,
          agentTenant: updated.agentTenant ?? mergedAgent.agentTenant,
          tenant_sip_id: updated.tenant_sip_id ?? mergedSettings.tenant_sip_id,
          rest_id: updated.rest_id ?? mergedSettings.rest_id,
          tenant_id: updated.tenant_id ?? mergedSettings.tenant_id,
          school_building_name:
            updated.school_building_name ?? mergedSettings.school_building_name,
          status: currentAgent.status || 'unknown',
          lastSeen: currentAgent.lastSeen || new Date().toISOString(),
        });

        toast.success('Configuration saved successfully');
        return true;
      } catch (error) {
        console.error('Failed to save config:', error);
        toast.error(error?.message || 'Failed to save configuration');
        return false;
      }
    },
    [agents, selectedAgent, processMessage]
  );

  return (
    <div className="direct-agent-view">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Direct Agent Control</h3>
        <div className="flex items-center text-sm text-gray-500">
          <span className="mr-2">Status:</span>
          <span
            className={`px-2 py-1 rounded-full text-xs ${
              mqttStatus === 'connected'
                ? 'bg-green-100 text-green-800'
                : mqttStatus === 'disconnected'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-800'
            }`}
          >
            {mqttStatus || 'unknown'}
          </span>
        </div>
      </div>

      {renderConnectionStatus()}

      {!mqttReady ? (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">Connecting to MQTT broker...</p>
            </div>
          </div>
        </div>
      ) : loading ? (
        <div className="flex justify-center py-8">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="mt-2 text-sm text-gray-600">Loading agents...</p>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 text-red-700 p-4 rounded">
          <p>Error loading agents: {error.message}</p>
        </div>
      ) : agents.length === 0 ? (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h2a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                No agents connected. Agents will appear here when they connect to the MQTT broker.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {agents.map(agent => (
            <AgentCard
              key={agent.id || agent.agent_id}
              agent={agent}
              onConfigClick={() => {
                setSelectedAgent(agent);
                setIsConfigModalOpen(true);
              }}
            />
          ))}
        </div>
      )}

      {/* Config Modal */}
      {selectedAgent && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 transition-opacity duration-300 ${isConfigModalOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto transform transition-transform duration-300">
            <div className="sticky top-0 bg-white border-b border-gray-200 flex justify-between items-center px-6 py-4 rounded-t-lg">
              <h3 className="text-lg font-semibold text-gray-900">
                Configure Agent: {selectedAgent.name || selectedAgent.agent_id}
              </h3>
              <button
                onClick={handleCloseConfig}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
                aria-label="Close"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <AgentConfigPanel
                agent={selectedAgent}
                onClose={handleCloseConfig}
                onSave={handleConfigSave}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// AgentCard component with better memoization
const AgentCard = memo(({ agent, onConfigClick }) => {
  const statusColor = useMemo(() => {
    switch (agent.status) {
      case 'online':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'offline':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'busy':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  }, [agent.status]);

  const formattedLastSeen = useMemo(() => {
    try {
      return new Date(agent.lastSeen).toLocaleString();
    } catch {
      return 'Unknown';
    }
  }, [agent.lastSeen]);

  const handleConfigClick = useCallback(() => {
    onConfigClick(agent);
  }, [agent, onConfigClick]);

  return (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200 hover:shadow-md transition-shadow duration-200">
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex justify-between items-center">
          <h4 className="text-sm font-semibold text-gray-900 truncate" title={agent.name}>
            {agent.name}
          </h4>
          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${statusColor}`}>
            {agent.status}
          </span>
        </div>
      </div>

      <div className="px-4 py-3 space-y-3">
        <div className="grid grid-cols-1 gap-3">
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Agent ID</dt>
            <dd className="mt-1 text-xs font-mono bg-gray-50 px-2 py-1 rounded text-gray-900">
              {agent.id}
            </dd>
          </div>

          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Last Seen</dt>
            <dd className="mt-1 text-sm text-gray-900">{formattedLastSeen}</dd>
          </div>

          {agent.features?.length > 0 && (
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Features
              </dt>
              <dd className="mt-1">
                <div className="flex flex-wrap gap-1">
                  {agent.features.slice(0, 3).map((feature, idx) => (
                    <span
                      key={idx}
                      className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md text-xs font-medium"
                    >
                      {feature}
                    </span>
                  ))}
                  {agent.features.length > 3 && (
                    <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-md text-xs">
                      +{agent.features.length - 3} more
                    </span>
                  )}
                </div>
              </dd>
            </div>
          )}
        </div>

        <div className="pt-2 border-t border-gray-100">
          <button
            onClick={handleConfigClick}
            className="w-full inline-flex justify-center items-center px-3 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            Configure
          </button>
        </div>
      </div>
    </div>
  );
});

AgentCard.displayName = 'AgentCard';

export default DirectAgentView;
